import { h, Fragment, Component, Element, Host, State, Prop, Watch, Event, Listen } from '@stencil/core';
import type { ComponentInterface, EventEmitter } from '@stencil/core';
import {
	mod, clamp,
	wait, debounce, throttle, deferredPromise,
	distanceFromPointToDomRect, distanceFromPointToPoint, domRectCenter, domRectIntersectionRatio
} from 'utils';

@Component({
	tag: 'app-slides',
	styleUrl: 'slides.scss',
	shadow: true
})
export class AppSlides implements ComponentInterface {
	@Element() root: HTMLAppSlidesElement;
	viewport?: HTMLElement;
	slidesContainer?: HTMLElement;

	/** Ensure that the most prominently visible slide is selected */
	@Prop() selectProminentSlide = false;

	/** Show arrows */
	@Prop() arrows = false;
	/**
	 * Paginate will scroll by a factor of the viewport size.
	 * Move will scroll enough to snap on the next slide in the intented direction.
	 * Select will move the slide selection towards the intended direction.
	 */
	@Prop() arrowBehavior: 'paginate' | 'move' | 'select' = 'paginate';
	@Prop() paginationFactor = 0.6;
	/** Arrows loop around like a carousel */
	@Prop() loop = false;

	/** Show bullets, which reflect the selected slide */
	@Prop() bullets = false;

	/** Click on a slide to select it */
	@Prop() clickToSelect = false;

	/**
	 * A group identifier for synchronizing multiple slides components
	 */
	@Prop() syncGroup?: string;
	/**
	 * Override synchronization group that this component controls
	 */
	@Prop() syncControls?: string;
	/**
	 * Override synchronization group that this component is controlled by
	 */
	@Prop() syncControlledBy?: string;

	/** Show autoplay toggle button */
	@Prop() autoplayToggle = false;
	/** Enable automatic movement */
	@Prop({ mutable: true, reflect: true }) autoplay = false;
	/** Default seconds waiting per slide when autoplaying */
	@Prop() autoplayDuration = 8;

	@State() slides: ReadonlyArray<HTMLElement>;

	@State() selectedSlide?: HTMLElement;
	@Event() slideSelected: EventEmitter<HTMLElement>;
	@Listen('slideSelected', { target: 'window' })
	syncWithOtherSlide(e: CustomEvent<HTMLElement>) {
		const otherSlides = e.composedPath()[0] as typeof this.root | undefined;
		if (!otherSlides || otherSlides === this.root)
			return;

		const otherSlidesControls = otherSlides.syncControls || otherSlides.syncGroup;
		const thisControlledBy = this.syncControlledBy || this.syncGroup;
		if (otherSlidesControls !== thisControlledBy || !thisControlledBy)
			return;

		const otherSlide = e.detail;
		let correspondingSlide: HTMLElement | undefined;
		if (otherSlide.dataset.slideId)
			correspondingSlide = this.slides.find(slide => slide.dataset.slideId === otherSlide.dataset.slideId);
		correspondingSlide ||= this.slides[otherSlide.assignedSlot!.assignedElements().indexOf(otherSlide)];
		if (!correspondingSlide || correspondingSlide === this.selectedSlide)
			return;

		this.selectedSlide = correspondingSlide;
		this.goToSlide(this.selectedSlide);
	}

	autoplaying = false;
	@Watch('autoplay')
	async updateAutoplay() {
		if (!this.autoplay || this.autoplaying)
			return;

		this.autoplaying = true;

		while (true) {
			let waitTime: number | undefined;
			if (this.arrowBehavior === 'select')
				waitTime = Number(this.selectedSlide?.dataset.waitTime);
			if (!Number.isFinite(waitTime))
				waitTime = this.autoplayDuration;
			await wait(waitTime! * 1_000);

			if (!this.autoplay) {
				this.autoplaying = false;
				return;
			}

			this.goDirection(+1);
		}
	}

	currentlyAutomaticallyScrolling = false;
	startAutomatedScrolling() {
		this.currentlyAutomaticallyScrolling = true;
		this.slidesContainer!.style.setProperty('pointer-events', 'none');
		this.slidesContainer!.blur();

		const deferred = deferredPromise<undefined>();
		const onScroll = debounce(() => {
			this.currentlyAutomaticallyScrolling = false;
			this.slidesContainer!.style.removeProperty('pointer-events');

			this.slidesContainer!.removeEventListener('scroll', onScroll);
			deferred.resolve(undefined);
		}, 150);
		// Call (debounced) in case scrolling never actually happens
		onScroll();
		this.slidesContainer!.addEventListener('scroll', onScroll);
		return deferred.promise;
	}

	goToSlide(slide: HTMLElement) {
		const containerCenter = domRectCenter(this.slidesContainer!.getBoundingClientRect());
		const slideCenter     = domRectCenter(slide.getBoundingClientRect());

		this.startAutomatedScrolling();
		this.slidesContainer!.scrollBy({
			behavior: 'smooth',
			left: slideCenter.x - containerCenter.x,
			top:  slideCenter.y - containerCenter.y
		});
	}

	goDirection(direction: 1 | -1) {
		if (this.arrowBehavior === 'select') {
			if (!this.selectedSlide)
				return;

			const index = this.slides.indexOf(this.selectedSlide);
			const newIndex =
				this.loop
					? mod(index + direction, this.slides.length)
					: clamp(0, index + direction, this.slides.length - 1);
			this.selectedSlide = this.slides[newIndex];
			this.slideSelected.emit(this.selectedSlide);
			this.goToSlide(this.selectedSlide);
			return;
		}

		this.startAutomatedScrolling().then(() => {
			if (this.selectProminentSlide) {
				this.selectedSlide = this.getMostProminentSlide();
				this.slideSelected.emit(this.selectedSlide);
			}
		});

		if (this.loop) {
			const goToStart = direction === +1 && !this.canScrollRight;
			const goToEnd   = direction === -1 && !this.canScrollLeft;
			if (goToStart || goToEnd) {
				if (goToStart) {
					this.slidesContainer!.scrollTo({
						behavior: 'smooth',
						left: 0
					});
				}
				else if (goToEnd) {
					this.slidesContainer!.scrollTo({
						behavior: 'smooth',
						left: this.slidesContainer!.scrollWidth
					});
				}
				return;
			}
		}

		if (this.arrowBehavior === 'paginate') {
			const { width } = this.slidesContainer!.getBoundingClientRect();
			this.slidesContainer!.scrollBy({
				behavior: 'smooth',
				left: direction * this.paginationFactor * width
			});
		}
		else if (this.arrowBehavior === 'move') {
			const slideMetrics = [...this.getSlideMetrics()];
			if (direction === -1)
				slideMetrics.reverse();
			// Find the first slide that is visible on the side that
			// the user intends to move away from
			const firstShowing = slideMetrics
				.findIndex(([_, metrics]) => metrics.intersectionRatio > 0.001);
			if (firstShowing === -1)
				return;
			// Find the first slide after that one which is not fully visible
			const firstNotFullyShowing = slideMetrics
				.slice(firstShowing)
				.find(([_, metrics]) => metrics.intersectionRatio <= 0.999);
			if (!firstNotFullyShowing)
				return;
			this.goToSlide(firstNotFullyShowing[0]);
		}
	}

	@State() canScrollLeft  = false;
	@State() canScrollRight = true;
	updateScrollableDirections() {
		this.canScrollLeft  = this.slidesContainer!.scrollLeft > 0;
		this.canScrollRight = Math.ceil(this.slidesContainer!.scrollLeft + this.slidesContainer!.clientWidth) < this.slidesContainer!.scrollWidth;
	}

	getSlideMetrics() {
		const slideMetrics = new Map<HTMLElement, {
			intersectionRatio: number,
			distanceFromCenter: number,
			distanceFromCenterToCenter: number
		}>();

		const slidesContainerRect = this.slidesContainer!.getBoundingClientRect();
		const slidesContainerCenter = domRectCenter(slidesContainerRect);

		this.slides.forEach(slide => {
			const rect = slide.getBoundingClientRect();
			const intersectionRatio = domRectIntersectionRatio(slidesContainerRect, rect);

			slide.classList.toggle('showing', intersectionRatio > 0);
			slide.classList.toggle('completely', intersectionRatio > 0.999); // 1px/1000px rounding error

			slideMetrics.set(slide, {
				intersectionRatio,
				distanceFromCenter: distanceFromPointToDomRect(slidesContainerCenter, rect),
				distanceFromCenterToCenter: distanceFromPointToPoint(slidesContainerCenter, domRectCenter(rect))
			});
		});

		return slideMetrics;
	}

	getMostProminentSlide() {
		this.updateScrollableDirections();

		const slideMetrics = this.getSlideMetrics();

		if (!this.canScrollLeft)
			return this.slides[0];
		else if (!this.canScrollRight)
			return this.slides[this.slides.length - 1];

		const topIntersecting = [...slideMetrics]
			.filter(([_, metrics]) => metrics.intersectionRatio !== 0)
			.sort((a, b) => b[1].intersectionRatio - a[1].intersectionRatio)
			.filter((entry, _, sortedEntries) => entry[1].intersectionRatio === sortedEntries[0][1].intersectionRatio);

		return topIntersecting
			.reduce((closestEntry, entry) =>
				entry[1].distanceFromCenter < closestEntry[1].distanceFromCenter
					? entry
					: closestEntry
			, topIntersecting[0])
			?.[0] as HTMLElement | undefined;
	}

	componentWillLoad() {
		this.slides = [...this.root.children].filter(child => child.slot === '') as any;
		this.selectedSlide =
			this.slides.find(slide => slide.classList.contains('initial'))
			?? this.slides[0];
	}

	componentDidLoad() {
		if (this.selectedSlide)
			this.goToSlide(this.selectedSlide);

		new ResizeObserver(debounce(() => {
			this.updateScrollableDirections();
		})).observe(this.viewport!);

		this.slidesContainer!.addEventListener('scroll', debounce(() => {
			this.updateScrollableDirections();
		}), { passive: true });

		this.slidesContainer!.addEventListener('scroll', throttle(() => {
			if (!this.selectProminentSlide || this.currentlyAutomaticallyScrolling)
				return;

			this.selectedSlide = this.getMostProminentSlide();
			this.slideSelected.emit(this.selectedSlide);
		}), { passive: true });

		this.slidesContainer!.addEventListener('click', e => {
			if (!this.clickToSelect)
				return;

			const slide = e.composedPath().find(slide => this.slides.includes(slide as any)) as HTMLElement | undefined;
			if (!slide)
				return;

			this.selectedSlide = slide;
			this.slideSelected.emit(this.selectedSlide);
			this.goToSlide(this.selectedSlide);
		});

		this.updateAutoplay();
	}

	render() {
		return (
			<Host>
				<div
					class='viewport'
					part='viewport'
					ref={e => this.viewport = e}
				>
					<div
						class={{
							'no-btns-arrows': Boolean(this.canScrollLeft || this.canScrollRight) ? false : true, 
							'slides' : true
						 }}
						part='slides'
						ref={e => this.slidesContainer = e}
					>
						<slot
							onSlotchange={() => {
								this.slides = [...this.root.children].filter(child => child.slot === '') as any;
								if (!this.slides.includes(this.selectedSlide as any))
									this.selectedSlide =
										this.getMostProminentSlide()
										?? this.slides.find(slide => slide.classList.contains('initial'))
										?? this.slides[0];
							}}
						/>
					</div>
				</div>

				{this.slides.length > 1 && (this.canScrollLeft || this.canScrollRight) &&
				<>
					{this.arrows &&
						<div
							class='arrows'
							part='arrows'
						>
							<button
								type='button'
								class='left'
								part='left arrow'
								onClick={() => this.goDirection(-1)}
								disabled={
									this.loop
										? false
										: this.arrowBehavior === 'select'
											? this.selectedSlide === this.slides[0]
											: !this.canScrollLeft
								}
							>
								<slot name='left-arrow'>
									<span><img src="/ui/www/assets/img/gold-arrow_short.svg" alt="arrow left" /></span>
								</slot>
								<span class='visually-hidden'>
									{ this.arrowBehavior === 'paginate'
										? 'Scroll to the left'
									: this.arrowBehavior === 'move'
										? 'Scroll to reveal the next slide on the left'
									: this.arrowBehavior === 'select'
										? 'Select the next slide on the left'
									: false
									}
								</span>
							</button>

							<button
								type='button'
								class='right'
								part='right arrow'
								onClick={() => this.goDirection(+1)}
								disabled={
									this.loop
										? false
										: this.arrowBehavior === 'select'
											? this.selectedSlide === this.slides[this.slides.length - 1]
											: !this.canScrollRight
								}
							>
								<slot name='right-arrow'>
									<span><img src="/ui/www/assets/img/gold-arrow_short.svg" alt="arrow right" /></span>
								</slot>
								<span class='visually-hidden'>
									{ this.arrowBehavior === 'paginate'
										? 'Scroll to the right'
									: this.arrowBehavior === 'move'
										? 'Scroll to reveal the next slide on the right'
									: this.arrowBehavior === 'select'
										? 'Select the next slide on the right'
									: false
									}
								</span>
							</button>
						</div>
					} 

					{this.bullets &&
						<div
							class='bullets'
							part='bullets'
						>
							{this.slides.map((slide, i) =>
								<button
									key={i}
									class='bullet'
									part={
										'bullet' +
										(this.selectedSlide === slide ? ' selected' : '')
									}
									type='button'
									onClick={() => {
										this.selectedSlide = slide;
										this.slideSelected.emit(this.selectedSlide);
										this.goToSlide(this.selectedSlide);
									}}
									aria-selected={'' + (this.selectedSlide === slide)}
								>
									<span class='visually-hidden'>
										Scroll to slide {i + 1}
									</span>
								</button>
							)}
						</div>
					}
				</>
				}

				{this.autoplayToggle &&
					<button
						type='button'
						class='autoplay-toggle'
						part={
							'autoplay-toggle' +
							(this.autoplay ? ' pause' : ' play')
						}
						onClick={() => {
							this.autoplay = !this.autoplay;
						}}
					>
						{this.autoplay
							?
							<>
								<slot name='pause'>
									<app-icon
										icon='pause'
										part='pause icon'
									/>
								</slot>
								<span class='visually-hidden'>
									Pause autoplay
								</span>
							</>
							:
							<>
								<slot name='play'>
									<app-icon
										icon='play'
										part='play icon'
									/>
								</slot>
								<span class='visually-hidden'>
									Toggle autoplay on
								</span>
							</>
						}
					</button>
				}
			</Host>
		);
	}
}
